/**
 * SessionDropdown Component
 *
 * Session dropdown for header right zone — per-status menu actions.
 * Renders phase-appropriate actions (transitions + Save & Exit) based on
 * the current session status. Transition actions open PhaseTransitionModal
 * for confirmation; Save & Exit fires immediately (no modal).
 *
 * @see SESSION_MENU_ACTIONS — per-status action definitions
 * @see PhaseTransitionModal — confirmation dialog for transitions
 */

// 1. External dependencies
import React, { useState, useCallback } from 'react';
import { Undo2, LogOut, Save, SquarePower } from 'lucide-react';

// 2. Internal absolute imports
import {
  Button,
  Dropdown, DropdownTrigger, DropdownMenu, DropdownItem,
} from '@enfomentis/ui';
import { SESSION_MENU_ACTIONS } from '@/constants';

// 3. Type imports
import type { SessionDraft } from '@/types/transient/SessionDraft';
import type { TradingPhase, TransitionConfig, SessionMenuAction } from '@/types/shared';

// 4. Relative imports
import { PhaseTransitionModal } from './PhaseTransitionModal';

// ===== ICON MAP =====

/** Maps action keys to Lucide icons for dropdown startContent */
const ACTION_ICONS: Record<SessionMenuAction['key'], React.ReactNode> = {
  'return-to-pre-trading': <Undo2 size={16} />,
  'end-trading-session': <LogOut size={16} />,
  'save-exit': <Save size={16} />,
};

// ===== COMPONENT =====

export interface SessionDropdownProps {
  /** Current session status — determines which menu items appear */
  sessionStatus: SessionDraft['status'];
  /** Phase transition handler — returns true on success, false on failure */
  onPhaseChange: (phase: TradingPhase) => Promise<boolean>;
  /** Save & Exit handler — flush pending data and navigate to dashboard */
  onSaveAndExit: () => Promise<void>;
}

/**
 * Session Dropdown — header right zone menu.
 *
 * Menu contents vary by session status:
 * - pre-trading: Save & Exit only
 * - live: Return to Pre-Trading, End Trading Session, Save & Exit
 * - post-trading / review / shelved: Save & Exit only
 */
export const SessionDropdown: React.FC<SessionDropdownProps> = ({
  sessionStatus,
  onPhaseChange,
  onSaveAndExit,
}) => {
  const menuActions = SESSION_MENU_ACTIONS[sessionStatus];

  // Pending transition — which confirmation modal is open
  const [pendingTransition, setPendingTransition] = useState<TransitionConfig | null>(null);

  const handleAction = useCallback((key: React.Key): void => {
    const keyStr = String(key);
    const action = menuActions.find(a => a.key === keyStr);
    if (!action) return;

    if (action.type === 'save-exit') {
      void onSaveAndExit().catch((err) => console.warn('SessionDropdown: onSaveAndExit unexpected error:', err));
    } else if (action.type === 'transition') {
      setPendingTransition(action.transitionConfig);
    }
  }, [menuActions, onSaveAndExit]);

  const handleTransitionClose = useCallback((): void => {
    setPendingTransition(null);
  }, []);

  const handleTransitionConfirm = useCallback(async (): Promise<boolean> => {
    if (!pendingTransition) return false;
    return onPhaseChange(pendingTransition.targetPhase);
  }, [pendingTransition, onPhaseChange]);

  return (
    <>
      <Dropdown placement="bottom-end">
        <DropdownTrigger>
          <Button variant="light" color="danger" size="sm" isIconOnly aria-label="Session menu">
            <SquarePower size={25} />
          </Button>
        </DropdownTrigger>
        <DropdownMenu aria-label="Session actions" onAction={handleAction}>
          {menuActions.map(action => (
            <DropdownItem key={action.key} startContent={ACTION_ICONS[action.key]}>
              {action.label}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>

      {/* Phase Transition Confirmation Modal */}
      {pendingTransition && (
        <PhaseTransitionModal
          isOpen
          onClose={handleTransitionClose}
          onConfirm={handleTransitionConfirm}
          title={pendingTransition.title}
          description={pendingTransition.description}
          confirmLabel={pendingTransition.confirmLabel}
          confirmColor={pendingTransition.confirmColor}
        />
      )}
    </>
  );
};
